import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MESSAGE_MARKET_LABELS,
  messageByShort,
  versionById,
  versionsFor,
} from '@/data/iso20022';
import { samplesForMessage, sampleById } from '@/data/samples';
import { usagesOfMessage } from '@/data/flows';
import { paymentsUsingMessage } from '@/data/relations';
import { paymentById } from '@/data/payments';
import { MessageIdPlate } from '@/components/MessageIdPlate';
import { PayloadInspector } from '@/components/PayloadInspector';
import { Tag } from '@/components/Chips';
import { ToggleTab } from '@/components/try/ToggleTab';
import { MessageEditWorkspace } from '@/views/TryEditorView';
import { editorPrimaryShort } from '@/lib/editorPairs';
import { messageWorkspaceHref } from '@/lib/messageWorkspace';
import { canonicalId, messageIdFromPayload, namespaceFor, parseMessageId } from '@/lib/messageId';
import { cn } from '@/lib/cn';
import { localizeFlow, useI18n, useT } from '@/i18n';
import { NotFoundView } from './NotFoundView';

const DIRECTION_KEYS: Record<string, string> = {
  'customer-to-bank': 'message.dirCustomerBank',
  'bank-to-customer': 'message.dirBankCustomer',
  'bank-to-bank': 'message.dirBankBank',
  'bank-to-csm': 'message.dirBankCsm',
};

const STATUS_KEYS = {
  current: 'message.versionCurrent',
  legacy: 'message.versionLegacy',
  upcoming: 'message.versionUpcoming',
} as const;

/**
 * Per-message workspace: View (XML) | Edit (form + XML), related flows, samples.
 */
export function MessageView() {
  const t = useT();
  const { locale } = useI18n();
  const { short } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tagQuery = searchParams.get('q') ?? '';
  const versionParam = searchParams.get('v');
  const sampleParam = searchParams.get('sample');
  const mode = searchParams.get('mode') === 'edit' ? 'edit' : 'view';
  const helpMode = searchParams.get('help') === '1';

  const message = short ? messageByShort(short) : undefined;
  const editPrimary =
    mode === 'edit' && short ? editorPrimaryShort(short) : short;
  const redirectEdit =
    mode === 'edit' && short && editPrimary && editPrimary !== short
      ? messageWorkspaceHref(editPrimary, {
          mode: 'edit',
          help: helpMode,
          v: versionParam ?? undefined,
          sample: sampleParam ?? undefined,
        })
      : null;

  const versions = message ? versionsFor(message) : [];
  const selectedVersion =
    (versionParam && message ? versionById(message, versionParam) : undefined) ??
    versions.find((v) => v.id === message?.id) ??
    versions[0];

  const samples = useMemo(() => {
    if (!short) return [];
    const all = samplesForMessage(short).filter((s) => !s.id.endsWith('-json'));
    if (!selectedVersion) return all;
    const matching = all.filter((s) => {
      const id = messageIdFromPayload(s.content);
      return id !== null && canonicalId(id) === canonicalId(selectedVersion.id);
    });
    return matching.length > 0 ? matching : all;
  }, [short, selectedVersion]);

  const sampleFromParam = sampleParam ? sampleById(sampleParam) : undefined;
  const initialIndex = Math.max(
    0,
    samples.findIndex((s) => s.id === sampleFromParam?.id),
  );

  const [activeSample, setActiveSample] = useState(initialIndex);
  const [edited, setEdited] = useState<string | null>(null);

  useEffect(() => {
    const idx = Math.max(
      0,
      samples.findIndex((s) => s.id === sampleParam),
    );
    setActiveSample(sampleParam ? idx : 0);
    setEdited(null);
  }, [short, selectedVersion?.id, sampleParam, samples]);

  if (redirectEdit) return <Navigate to={redirectEdit} replace />;
  if (!message || !selectedVersion) return <NotFoundView />;

  const sample = samples[activeSample] ?? samples[0];
  const usages = usagesOfMessage(message.short).map((u) => {
    const flow = localizeFlow(u.flow, locale);
    const steps = u.steps.map((s) => flow.steps.find((ls) => ls.n === s.n) ?? s);
    return { flow, steps };
  });
  const isAck = message.short === 'pacs.002';
  const parts = parseMessageId(selectedVersion.id);
  const payloadId = sample ? messageIdFromPayload(sample.content) : null;
  const sampleMatchesVersion =
    !sample || (payloadId !== null && canonicalId(payloadId) === canonicalId(selectedVersion.id));

  function patchParams(mutate: (next: URLSearchParams) => void) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  }

  function selectVersion(id: string) {
    patchParams((next) => {
      if (id === message!.id) next.delete('v');
      else next.set('v', id);
    });
  }

  function setMode(next: 'view' | 'edit') {
    patchParams((p) => {
      if (next === 'edit') p.set('mode', 'edit');
      else p.delete('mode');
    });
  }

  function setHelp(on: boolean) {
    patchParams((p) => {
      if (on) p.set('help', '1');
      else p.delete('help');
    });
  }

  function selectSample(id: string, index: number) {
    setActiveSample(index);
    setEdited(null);
    patchParams((p) => {
      p.set('sample', id);
    });
  }

  return (
    <div className="page-fluid">
      <header className="max-w-4xl">
        <p className="eyebrow">
          {t(`area.${message.area}`)}
          {isAck && ` · ${t('message.ackEyebrow')}`}
        </p>
        <h1 className="mt-2 text-3xl font-bold sm:text-4xl">{selectedVersion.schemaName}</h1>
        <div className="mt-5">
          <MessageIdPlate id={selectedVersion.id} />
        </div>
        <p className="mt-5 text-[15px] leading-relaxed text-muted">{message.purpose}</p>
        {isAck && (
          <p className="mt-3 border-l-2 border-jade pl-3 text-[13px] leading-relaxed">{t('message.ackNote')}</p>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="flex gap-px" role="group" aria-label={t('message.workspaceMode')}>
            <ToggleTab active={mode === 'view'} onClick={() => setMode('view')} size="sm">
              {t('message.modeView')}
            </ToggleTab>
            <ToggleTab active={mode === 'edit'} onClick={() => setMode('edit')} size="sm">
              {t('message.modeEdit')}
            </ToggleTab>
          </div>
          {mode === 'edit' && (
            <button
              type="button"
              onClick={() => setHelp(!helpMode)}
              className={cn(
                'border px-2 py-1 font-mono text-[10px] uppercase tracking-wider',
                helpMode
                  ? 'border-ink bg-ink text-white'
                  : 'border-rule bg-surface text-muted hover:border-ink hover:text-ink',
              )}
            >
              {t('message.helpMode')}
            </button>
          )}
          {message.short === 'pacs.008' && (
            <Link to="/compare/pacs.008" className="font-mono text-[11px] text-signal hover:underline">
              {t('nav.compareVersions')} →
            </Link>
          )}
        </div>

        {paymentsUsingMessage(message.short).length > 0 && (
          <div className="mt-5">
            <p className="eyebrow mb-2">{t('message.startWithStory')}</p>
            <ul className="flex flex-wrap gap-1.5">
              {paymentsUsingMessage(message.short).map((id) => (
                <li key={id}>
                  <Link
                    to={`/payment/${id}`}
                    className="border border-rule bg-surface px-2 py-1 text-[13px] hover:border-ink"
                  >
                    {paymentById(id)?.name[locale] ?? id}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {usages.length > 0 && (
          <div className="mt-5">
            <p className="eyebrow mb-2">{t('message.appearsIn')}</p>
            <ul className="flex flex-wrap gap-1.5">
              {usages.map(({ flow, steps }) => (
                <li key={flow.id}>
                  <Link
                    to={`/flows/${flow.id}?step=${steps[0].n}`}
                    className="inline-flex items-center gap-1.5 border border-rule bg-surface px-2 py-1 text-[13px] hover:border-ink"
                  >
                    <span>{flow.name}</span>
                    <span className="font-mono text-[10px] text-muted">
                      {steps.map((s) => String(s.n).padStart(2, '0')).join(' · ')}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <section className="mt-8 max-w-4xl">
        <h2 className="eyebrow mb-1">{t('message.versions')}</h2>
        <p className="mb-2 max-w-2xl text-[13px] leading-relaxed text-muted">
          {mode === 'edit' ? t('message.versionsEditLead') : t('message.versionsLead')}
        </p>
        {mode === 'view' && (
          <p className="mb-3 flex flex-wrap gap-x-3 gap-y-1 text-[12px]">
            <a
              href="https://www.iso20022.org/iso-20022-message-definitions"
              target="_blank"
              rel="noreferrer"
              className="text-signal hover:underline"
            >
              {t('message.versionsXsd')} ↗
            </a>
            <a
              href="https://www.swift.com/standards/iso-20022/iso-20022-standards"
              target="_blank"
              rel="noreferrer"
              className="text-signal hover:underline"
            >
              {t('message.versionsSwift')} ↗
            </a>
            <Link to="/glossary?id=swift" className="text-signal hover:underline">
              {t('message.versionsGlossary')} →
            </Link>
          </p>
        )}
        {versions.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {versions.map((v) => {
              const vp = parseMessageId(v.id);
              const active = v.id === selectedVersion.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => selectVersion(v.id)}
                  className={cn(
                    'min-w-[10.5rem] border px-3 py-2 text-left transition-colors',
                    active ? 'border-ink bg-ink text-white' : 'border-rule bg-surface hover:border-ink',
                  )}
                >
                  <span className="block font-mono text-[13px] font-medium tnum">{v.id}</span>
                  <span
                    className={cn(
                      'mt-1 block font-mono text-[10px] uppercase tracking-wider',
                      active ? 'text-white/70' : 'text-muted',
                    )}
                  >
                    {t(STATUS_KEYS[v.status])}
                    {vp.variant ? ` · ${t('message.variantShort', { variant: vp.variant })}` : ''}
                    {vp.version ? ` · v${vp.version}` : ''}
                    {vp.country && vp.guideline ? ` · ${vp.country}.${vp.guideline}` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="panel mt-3 space-y-2 p-4">
          <p className="text-[13px] leading-relaxed">
            {selectedVersion.notes[locale] ?? selectedVersion.notes.en}
          </p>
          {selectedVersion.markets.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-wider text-muted">
                {t('message.markets')}
              </span>
              {selectedVersion.markets.map((m) => (
                <Tag key={m}>
                  {MESSAGE_MARKET_LABELS[m]?.[locale] ?? MESSAGE_MARKET_LABELS[m]?.en ?? m}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </section>

      {mode === 'edit' ? (
        <div className="mt-8">
          <MessageEditWorkspace
            short={message.short}
            versionId={selectedVersion.id}
            embedded
            helpMode={helpMode}
          />
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-6 xl:grid-cols-[420px_1fr]">
            <div className="space-y-5">
              <section className="panel p-4">
                <h2 className="eyebrow mb-3">{t('message.facts')}</h2>
                <dl className="space-y-2.5 text-[13px]">
                  <Fact term={t('message.root')} value={<code>{message.rootElement}</code>} />
                  <Fact
                    term={t('message.direction')}
                    value={
                      DIRECTION_KEYS[message.direction]
                        ? t(DIRECTION_KEYS[message.direction])
                        : message.direction
                    }
                  />
                  <Fact
                    term={t('message.namespace')}
                    value={<code className="break-all text-[11px]">{namespaceFor(selectedVersion.id)}</code>}
                  />
                  {parts.valid && (
                    <>
                      <Fact term={t('message.variant')} value={<code>{parts.variant}</code>} />
                      <Fact term={t('message.version')} value={<code>{parts.version}</code>} />
                      {parts.country ? (
                        <Fact term={t('message.country')} value={<code>{parts.country}</code>} />
                      ) : null}
                      {parts.guideline ? (
                        <Fact term={t('message.guideline')} value={<code>{parts.guideline}</code>} />
                      ) : null}
                    </>
                  )}
                </dl>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {message.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </div>
              </section>

              <section className="panel p-4">
                <h2 className="eyebrow mb-2">{t('message.required')}</h2>
                <p className="mb-3 text-[12px] leading-relaxed text-muted">{t('message.requiredHint')}</p>
                <ul className="space-y-1">
                  {message.requiredPaths.map((p) => (
                    <li
                      key={p}
                      className="break-all border-l-2 border-rule pl-2 font-mono text-[11px] leading-relaxed"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              </section>

              {usages.length > 0 && (
                <section className="panel p-4">
                  <h2 className="eyebrow mb-2">{t('message.usage')}</h2>
                  <ul className="space-y-3">
                    {usages.map(({ flow, steps }) => (
                      <li key={flow.id}>
                        <Link
                          to={`/flows/${flow.id}?step=${steps[0].n}`}
                          className="text-[13px] font-medium text-signal hover:underline"
                        >
                          {flow.name}
                        </Link>
                        <ul className="mt-1 space-y-0.5">
                          {steps.map((s) => (
                            <li key={s.n}>
                              <Link
                                to={`/flows/${flow.id}?step=${s.n}`}
                                className="font-mono text-[11px] text-muted hover:text-ink hover:underline"
                              >
                                {String(s.n).padStart(2, '0')} · {s.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            <div className="min-w-0">
              {sample ? (
                <>
                  {!sampleMatchesVersion && (
                    <p className="mb-2 border border-ochre bg-ochre/10 px-3 py-2 text-[12px] leading-relaxed text-ink">
                      {t('message.sampleVersionMismatch', { version: selectedVersion.id })}
                    </p>
                  )}
                  {samples.length > 1 && (
                    <div className="mb-2 flex flex-wrap gap-px">
                      {samples.map((s, i) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => selectSample(s.id, i)}
                          className={
                            i === activeSample
                              ? 'border border-ink bg-ink px-3 py-1.5 font-mono text-[11px] text-white'
                              : 'border border-rule bg-surface px-3 py-1.5 font-mono text-[11px] text-muted hover:border-ink'
                          }
                        >
                          {s.format.toUpperCase()} · {s.label.replace(/\s*\(JSON\)\s*$/i, '')}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="h-[calc(100dvh-8rem)] min-h-[560px]">
                    <PayloadInspector
                      key={sample.id}
                      content={edited ?? sample.content}
                      format={sample.format}
                      title={sample.label}
                      description={`${sample.description} ${t('message.xmlJsonHint')}`}
                      onContentChange={setEdited}
                      initialFilter={tagQuery}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                    {sample.format === 'xml' && (
                      <Link
                        to={messageWorkspaceHref(message.short, {
                          mode: 'edit',
                          sample: sample.id,
                        })}
                        className="font-mono text-[11px] text-signal hover:underline"
                      >
                        {t('message.openEditor')}
                      </Link>
                    )}
                    <p className="font-mono text-[11px] text-muted">
                      {t('message.editHint')} {edited !== null && `${t('message.modified')} `}
                      <button
                        type="button"
                        onClick={() => setEdited(null)}
                        className="text-signal hover:underline"
                      >
                        {t('message.reset')}
                      </button>
                    </p>
                  </div>
                </>
              ) : (
                <div className="panel px-4 py-8 text-sm text-muted">
                  {t('message.noSample', { short: message.short })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Fact({ term, value }: { term: string; value: ReactNode }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-muted">{term}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}
